/**
 * AuditTab — PR4 audit log surface inside Knowledge Base.
 *
 * Pulls /api/actions/audit and renders newest-first. Per-row:
 *   - Status pill (succeeded / failed / rejected / pending / executing)
 *   - Title + summary
 *   - Kind badge, user, timestamps
 *   - Expandable JSON of original_params vs edited_params vs final_params
 *
 * Filters: kind, status, username (admin only). Pagination via offset.
 */

import { useEffect, useState, useCallback } from 'react'
import { Loader2, RefreshCw, AlertCircle, ChevronRight, ChevronDown } from 'lucide-react'
import { getActionAudit } from '../../lib/api'

const STATUS_STYLES = {
  succeeded: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
  failed:    'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
  rejected:  'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  approved:  'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
  pending:   'bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300',
  executing: 'bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300',
}

function fmt(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function AuditTab() {
  const [entries, setEntries] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [kindFilter, setKindFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [expanded, setExpanded] = useState(() => new Set())

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const resp = await getActionAudit({
        kind: kindFilter || undefined,
        status: statusFilter || undefined,
        limit: 100,
      })
      setEntries(resp.entries || [])
    } catch (e) {
      setError(String(e?.message || e))
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [kindFilter, statusFilter])

  useEffect(() => { load() }, [load])

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Action audit log</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Every Action Card approval — created, edited, approved, executed.
            {entries?.length != null && <span> · <span className="tabular-nums">{entries.length}</span> entries</span>}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={kindFilter}
          onChange={e => setKindFilter(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised"
        >
          <option value="">All kinds</option>
          <option value="create_dep_invoice">DEP invoice</option>
          <option value="create_shp_invoice">SHP invoice</option>
          <option value="create_pro_forma">Pro-forma</option>
          <option value="create_cash_receipt">Cash receipt</option>
          <option value="send_email">Email</option>
          <option value="update_sales_order">Update SO</option>
          <option value="create_quote">Quote</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised"
        >
          <option value="">All statuses</option>
          <option value="succeeded">Succeeded</option>
          <option value="failed">Failed</option>
          <option value="rejected">Rejected</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {loading && entries == null ? (
        <div className="py-12 flex items-center justify-center text-gray-400">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading audit history…
        </div>
      ) : entries && entries.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No audit entries yet. Approve an Action Card and it&apos;ll show up here.
        </div>
      ) : entries ? (
        <ul className="space-y-2">
          {entries.map(e => (
            <li key={e.id} className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setExpanded(s => {
                  const next = new Set(s)
                  if (next.has(e.id)) next.delete(e.id); else next.add(e.id)
                  return next
                })}
                className="w-full px-3 py-2 flex items-start gap-2 hover:bg-gray-50 dark:hover:bg-surface-dark-raised text-left"
              >
                <span className="mt-0.5 text-gray-400">
                  {expanded.has(e.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold ${STATUS_STYLES[e.status] || 'bg-gray-100 text-gray-600'}`}>
                      {e.status}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 dark:text-gray-500">{e.kind}</span>
                    {e.high_value && <span className="px-1 rounded bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 text-[10px] uppercase font-semibold">high-value</span>}
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0">{e.title || e.action_id}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>{e.username}</span>
                    <span className="mx-1.5">·</span>
                    <span>{fmt(e.created_at)}</span>
                    {e.completed_at && (
                      <>
                        <span className="mx-1.5">·</span>
                        <span>completed {fmt(e.completed_at)}</span>
                      </>
                    )}
                  </div>
                  {e.summary && (
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 truncate">{e.summary}</div>
                  )}
                </div>
              </button>
              {expanded.has(e.id) && (
                <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-900 bg-gray-50/50 dark:bg-surface-dark-raised/50 text-[11px] space-y-2">
                  {e.error && (
                    <DetailBlock label="Error" tone="red">
                      <pre className="whitespace-pre-wrap">{e.error?.message || JSON.stringify(e.error, null, 2)}</pre>
                    </DetailBlock>
                  )}
                  {e.result && (
                    <DetailBlock label="Result"><pre>{JSON.stringify(e.result, null, 2)}</pre></DetailBlock>
                  )}
                  {e.final_params && (
                    <DetailBlock label="Final params (server-validated)"><pre>{JSON.stringify(e.final_params, null, 2)}</pre></DetailBlock>
                  )}
                  {e.edited_params && (
                    <DetailBlock label="User edits"><pre>{JSON.stringify(e.edited_params, null, 2)}</pre></DetailBlock>
                  )}
                  {e.original_params && (
                    <DetailBlock label="Original params (from agent)"><pre>{JSON.stringify(e.original_params, null, 2)}</pre></DetailBlock>
                  )}
                  <div className="text-[10px] text-gray-400 font-mono">action_id: {e.action_id}</div>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function DetailBlock({ label, tone, children }) {
  return (
    <div>
      <div className={'text-[10px] uppercase tracking-wide font-semibold mb-0.5 ' + (tone === 'red' ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400')}>{label}</div>
      <div className={'rounded p-2 overflow-auto max-h-64 ' + (tone === 'red' ? 'bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-200' : 'bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 font-mono')}>
        {children}
      </div>
    </div>
  )
}
